import { useState } from "react";
import { motion } from "framer-motion";

const W = 680;
const H = 220;
const PAD_T = 18;
const PAD_B = 30;
const PAD_X = 10;
const INNER_H = H - PAD_T - PAD_B;
const EASE = [0.16, 1, 0.3, 1];

const fmtDay = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });

const smoothPath = (pts) => {
  if (pts.length < 2) return "";
  let d = `M ${pts[0][0]} ${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const [x0, y0] = pts[i];
    const [x1, y1] = pts[i + 1];
    const mx = (x0 + x1) / 2;
    d += ` C ${mx} ${y0}, ${mx} ${y1}, ${x1} ${y1}`;
  }
  return d;
};

export const SalesChart = ({ data }) => {
  const [hover, setHover] = useState(null);
  const maxCount = Math.max(1, ...data.map((d) => d.count));
  const maxRev = Math.max(1, ...data.map((d) => d.revenue));
  const step = (W - PAD_X * 2) / data.length;
  const barW = Math.max(4, step * 0.52);
  const x = (i) => PAD_X + i * step + step / 2;
  const yCount = (v) => PAD_T + INNER_H * (1 - v / maxCount);
  const yRev = (v) => PAD_T + INNER_H * (1 - v / maxRev);
  const linePts = data.map((d, i) => [x(i), yRev(d.revenue)]);

  return (
    <motion.div
      className="chart-wrap"
      data-testid="sales-chart"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: EASE }}
    >
      <div className="chart-legend">
        <span><i className="lg-bar" /> Ventes</span>
        <span><i className="lg-line" /> Chiffre d'affaires (€)</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="chart-svg" role="img" aria-label="Ventes des 30 derniers jours">
        {[0.25, 0.5, 0.75].map((f) => (
          <line key={f} x1={PAD_X} x2={W - PAD_X} y1={PAD_T + INNER_H * f} y2={PAD_T + INNER_H * f} className="chart-grid" />
        ))}
        {data.map((d, i) => (
          <rect
            key={d.date}
            data-testid={`chart-bar-${d.date}`}
            x={x(i) - barW / 2}
            y={yCount(d.count)}
            width={barW}
            height={Math.max(2, PAD_T + INNER_H - yCount(d.count))}
            rx={2.5}
            className="chart-bar"
          />
        ))}
        <path d={smoothPath(linePts)} className="chart-line" fill="none" />
        {hover !== null && <circle cx={linePts[hover][0]} cy={linePts[hover][1]} r={4.5} className="chart-dot" />}
        {data.map((d, i) =>
          (i % 5 === 0 || i === data.length - 1) ? (
            <text key={`t-${d.date}`} x={x(i)} y={H - 10} textAnchor="middle" className="chart-x">
              {fmtDay(d.date)}
            </text>
          ) : null
        )}
        {data.map((d, i) => (
          <rect
            key={`h-${d.date}`}
            x={PAD_X + i * step}
            y={PAD_T}
            width={step}
            height={INNER_H}
            fill="transparent"
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(null)}
          />
        ))}
      </svg>
      {hover !== null && (
        <div className="chart-tooltip" data-testid="chart-tooltip" style={{ left: `${(x(hover) / W) * 100}%` }}>
          <b>{fmtDay(data[hover].date)}</b>
          <span>
            {data[hover].count} vente{data[hover].count > 1 ? "s" : ""} · {data[hover].revenue.toLocaleString("fr-FR")} €
          </span>
        </div>
      )}
    </motion.div>
  );
};
